import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import { Cart } from './_types';
import { denormalizeCart, normalizeCart } from './normalizers';
import { normalizeSearchParams } from './normalizers';

interface FetchCartOptions {
  id: Cart['id'];
}

export const fetchCart = async ({ id }: FetchCartOptions): Promise<Cart> => {
  const response = await api.get(`/api/v4/carts/${id}`);
  return normalizeCart(response.data);
};

interface FetchCartsOptions {
  page?: number;
  perPage?: number;
  search?: string;
  userId?: string;
}

export const fetchCarts = async ({
  page,
  perPage,
  userId,
}: FetchCartsOptions): Promise<Cart[]> => {
  const params = {
    q: { s: 'created_at desc' },
    page: page || 1,
    per: perPage || 10,
    user_id: userId || '',
  };

  const response = await api.get(`/api/v4/carts`, { params });
  const carts = response.data?.cart
    ? response.data.cart.map((cart) => normalizeCart(cart))
    : response.data.map((cart) => normalizeCart(cart));

  return carts;
};

export const createCart = async (cart: Partial<Cart>): Promise<Cart> => {
  const data = denormalizeCart(cart);
  const response = await api.post(`/api/v4/carts`, { cart: data });
  return normalizeCart(response.data);
};

export const updateCart = async (cart: Partial<Cart>): Promise<Cart> => {
  const data = denormalizeCart(cart);
  const response = await api.put(`/api/v4/carts/${cart.id}`, { cart: data });
  return normalizeCart(response.data);
};

export const deleteCart = async (cartId: Cart['id']): Promise<void> => {
  await api.delete(`/api/v4/carts/${cartId}`);
};

export const getIntent = async (cart: Partial<Cart>): Promise<Cart> => {
  const data = denormalizeCart(cart);

  const response = await api.post(`/api/v4/carts/get_intent`, {
    cart: data,
  });

  return normalizeCart(response.data);
};

interface SearchResult {
  data: {
    json: Record<string, any>;
  };
  term: string;
  aliases: string[];
  type: string;
}

export const searchCartItem = async ({
  search,
  types,
  params = {},
}: {
  search: string;
  types?: string[];
  params?: Record<string, any>;
}): Promise<any[]> => {
  const searchTypes = types?.length
    ? types
    : ['group', 'package', 'product', 'plan'];

  const serverTypes = searchTypes.map((type) =>
    type === 'plan' ? 'plans' : type
  );

  const processedParams = normalizeSearchParams(params);

  const response = await api.get('/sm/search', {
    params: {
      types: serverTypes,
      limit: 100,
      no_limit: true,
      term: search,
      page: 1,
      ...processedParams,
    },
  });

  const results: Array<Record<string, any>> = [];

  searchTypes.forEach((type) => {
    const resultType = type === 'plan' ? 'plans' : type;
    if (response.data.results[resultType]) {
      response.data.results[resultType].forEach((item: SearchResult) => {
        results.push(
          camelcaseKeys(
            {
              ...item.data.json,
              type,
              term: item.term,
              aliases: item.aliases,
            },
            { deep: true }
          )
        );
      });
    }
  });

  return results;
};

export const checkPayment = async (params: {
  setup_intent?: string;
  setup_intent_secret?: string;
  payment_intent?: string;
  payment_intent_secret?: string;
  cart_id?: Cart['id'];
}): Promise<any> => {
  const response = await api.get('/api/v4/carts/check_payment', { params });
  return response.data;
};

export const confirmCart = async (cartId: Cart['id']) => {
  const response = await api.post(`/api/v4/carts/${cartId}/confirm`);
  return response.data;
};

export const confirmTerminalPayment = async (
  cartId: Cart['id'],
  terminalId: string | number,
  isSetupIntent?: boolean,
  simulatedCardNumber?: string
): Promise<any> => {
  const endpoint = isSetupIntent
    ? 'process_card_present_setup_intent'
    : 'process_card_present';
  const response = await api.post(`/api/v4/carts/${cartId}/${endpoint}`, {
    card_reader_id: terminalId,
    simulated_card_number: simulatedCardNumber,
  });
  return response.data;
};

export const processTerminalSetupIntent = async (
  terminalId: string | number,
  setupIntentId: string
): Promise<any> => {
  const response = await api.post(
    '/api/v4/carts/process_terminal_setup_intent',
    {
      card_reader_id: terminalId,
      setup_intent_id: setupIntentId,
    }
  );
  return response.data;
};

export const checkSetupIntentStatus = async (
  setupIntentId: string
): Promise<{ id: string; status: string }> => {
  const response = await api.get('/api/v4/carts/check_setup_intent_status', {
    params: { setup_intent_id: setupIntentId },
  });
  return response.data;
};

export const setReaderDisplay = async (
  cartId: Cart['id'],
  terminalId: string | number
): Promise<any> => {
  const response = await api.post(
    `/api/v4/carts/${cartId}/set_reader_display`,
    {
      card_reader_id: terminalId,
    }
  );
  return response.data;
};
